import {
  ArrowLeftOutlined,
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Input, Tooltip } from 'antd';
import { useCallback, useMemo, useRef, useState } from 'react';

import { ICONS } from '@app/constants';
import type { NCrmTasks } from '@app/services/rtkQuery/CrmTasks/types';

import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { ChangeEvent, FC, KeyboardEvent } from 'react';

import './TaskTypeDropdown.scss';

const DEFAULT_COLOR = '#1677ff';
const DEFAULT_ICON = 'fa-solid fa-tag';
const NEW_TYPE_DEFAULT_COLOR = '#8c8c8c';

interface TaskTypeDropdownProps {
  taskTypes: readonly NCrmTasks.ModelCrmTaskType[];
  selectedId: number | null;
  onSelect(type: NCrmTasks.ModelCrmTaskType): void;
  onCreate(params: {
    iconId: string;
    color: string;
    name: string;
  }): Promise<NCrmTasks.ModelCrmTaskType | null>;
  onEdit(type: NCrmTasks.ModelCrmTaskType): Promise<void>;
  onDelete(id: number): Promise<void>;
  onClose(): void;
}

type EditingTarget = number | 'new';

export const TaskTypeDropdown: FC<TaskTypeDropdownProps> = ({
  taskTypes,
  selectedId,
  onSelect,
  onCreate,
  onEdit,
  onDelete,
  onClose,
}) => {
  // ── Picker view state ──────────────────────────────────────────────────────
  const [showPicker, setShowPicker] = useState(false);
  const [editingTarget, setEditingTarget] = useState<EditingTarget | null>(
    null
  );
  const [pickerIcon, setPickerIcon] = useState(DEFAULT_ICON);
  const [pickerColor, setPickerColor] = useState(DEFAULT_COLOR);
  const [iconSearch, setIconSearch] = useState('');
  const colorInputRef = useRef<HTMLInputElement>(null);

  // ── New type draft ─────────────────────────────────────────────────────────
  const [draftIcon, setDraftIcon] = useState(DEFAULT_ICON);
  const [draftColor, setDraftColor] = useState(NEW_TYPE_DEFAULT_COLOR);
  const [newName, setNewName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // ── Local edits for existing types ────────────────────────────────────────
  const [localNames, setLocalNames] = useState<Record<number, string>>({});
  const [localIconColors, setLocalIconColors] = useState<
    Record<number, { IconId: string; Color: string }>
  >({});

  // Row is selectable by default; renaming starts only via the edit button so a
  // single click on the row never gets hijacked by the name input.
  const [editingNameId, setEditingNameId] = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Merge local overrides into the displayed types
  const effectiveTypes = useMemo(
    () =>
      taskTypes.map((t) => {
        const ic = localIconColors[t.Id];
        return {
          ...t,
          IconId: ic?.IconId ?? t.IconId,
          Color: ic?.Color ?? t.Color,
          Name: t.Id in localNames ? localNames[t.Id] : t.Name,
        };
      }),
    [taskTypes, localNames, localIconColors]
  );

  const filteredIcons = useMemo(() => {
    const q = iconSearch.trim().toLowerCase();
    if (!q) return ICONS;
    return ICONS.filter(
      ({ id, code }) =>
        id.toLowerCase().includes(q) || code.toLowerCase().includes(q)
    );
  }, [iconSearch]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const openPicker = useCallback(
    (target: EditingTarget) => {
      setEditingTarget(target);
      if (target === 'new') {
        setPickerIcon(draftIcon);
        setPickerColor(draftColor);
      } else {
        const ic = localIconColors[target];
        const type = taskTypes.find((t) => t.Id === target);
        setPickerIcon(ic?.IconId ?? type?.IconId ?? DEFAULT_ICON);
        setPickerColor(ic?.Color ?? type?.Color ?? DEFAULT_COLOR);
      }
      setIconSearch('');
      setShowPicker(true);
    },
    [draftIcon, draftColor, localIconColors, taskTypes]
  );

  const handleBack = useCallback(() => {
    setShowPicker(false);
    setEditingTarget(null);
  }, []);

  const handleIconSelect = useCallback(
    (code: string) => {
      if (editingTarget === null) {
        setShowPicker(false);
        return;
      }

      if (editingTarget === 'new') {
        setDraftIcon(code);
        setDraftColor(pickerColor);
      } else {
        const typeId = editingTarget;
        const type = taskTypes.find((t) => t.Id === typeId);
        if (type) {
          const updatedType: NCrmTasks.ModelCrmTaskType = {
            Id: type.Id,
            IconId: code,
            Color: pickerColor,
            Name: localNames[typeId] ?? type.Name,
          };
          setLocalIconColors((prev) => ({
            ...prev,
            [typeId]: { IconId: code, Color: pickerColor },
          }));
          void onEdit(updatedType);
        }
      }

      setShowPicker(false);
      setEditingTarget(null);
    },
    [editingTarget, pickerColor, taskTypes, localNames, onEdit]
  );

  const handlePickerColorChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setPickerColor(e.target.value);
    },
    []
  );

  const handlePickerHexInput = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      if (/^#[0-9A-Fa-f]{0,6}$/.test(val)) setPickerColor(val);
    },
    []
  );

  const handleSelect = useCallback(
    (type: NCrmTasks.ModelCrmTaskType) => {
      onSelect(type);
      onClose();
    },
    [onSelect, onClose]
  );

  const handleNameChange = useCallback((typeId: number, value: string) => {
    setLocalNames((prev) => ({ ...prev, [typeId]: value }));
  }, []);

  const handleNameBlur = useCallback(
    (originalType: NCrmTasks.ModelCrmTaskType) => {
      const localName = localNames[originalType.Id];
      if (localName === undefined) return;
      const trimmed = localName.trim();
      if (!trimmed || trimmed === (originalType.Name ?? '')) return;
      const ic = localIconColors[originalType.Id];
      const updatedType: NCrmTasks.ModelCrmTaskType = {
        Id: originalType.Id,
        IconId: ic?.IconId ?? originalType.IconId,
        Color: ic?.Color ?? originalType.Color,
        Name: trimmed,
      };
      void onEdit(updatedType);
    },
    [localNames, localIconColors, onEdit]
  );

  const handleDelete = useCallback(
    async (id: number) => {
      if (isDeleting) return;
      setIsDeleting(true);
      try {
        await onDelete(id);
        setConfirmDeleteId(null);
        if (editingNameId === id) setEditingNameId(null);
      } finally {
        setIsDeleting(false);
      }
    },
    [editingNameId, isDeleting, onDelete]
  );

  // Creation is an explicit action (button / Enter), never a side effect of
  // losing focus. On success the dropdown stays open so the refreshed list
  // shows the new type, and the draft resets for the next one.
  const handleCreate = useCallback(async () => {
    const trimmed = newName.trim();
    if (!trimmed || isCreating) return;
    setIsCreating(true);
    try {
      const created = await onCreate({
        iconId: draftIcon,
        color: draftColor,
        name: trimmed,
      });
      if (created) {
        setNewName('');
        setDraftIcon(DEFAULT_ICON);
        setDraftColor(NEW_TYPE_DEFAULT_COLOR);
      }
    } finally {
      setIsCreating(false);
    }
  }, [newName, isCreating, draftIcon, draftColor, onCreate]);

  const handleNewNameKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        void handleCreate();
      }
    },
    [handleCreate]
  );

  const canCreate = newName.trim().length > 0 && !isCreating;

  // ── Icon picker view ───────────────────────────────────────────────────────

  if (showPicker) {
    return (
      <div className="TaskTypeDropdown TaskTypeDropdown--picker">
        <div className="TaskTypeDropdown__picker-header">
          <button
            type="button"
            className="TaskTypeDropdown__back-btn"
            onClick={handleBack}
          >
            <ArrowLeftOutlined />
          </button>
          <span className="TaskTypeDropdown__picker-title">Иконка и цвет</span>
        </div>

        <div className="TaskTypeDropdown__color-row">
          <div className="TaskTypeDropdown__color-wrap">
            <button
              type="button"
              className="TaskTypeDropdown__color-swatch"
              style={{ background: pickerColor }}
              title="Выбрать цвет"
              onClick={() => colorInputRef.current?.click()}
            />
            <input
              ref={colorInputRef}
              type="color"
              value={pickerColor}
              onChange={handlePickerColorChange}
              className="TaskTypeDropdown__color-native"
              tabIndex={-1}
            />
          </div>
          <span className="TaskTypeDropdown__hex-label">HEX</span>
          <input
            type="text"
            className="TaskTypeDropdown__hex-input"
            value={pickerColor}
            onChange={handlePickerHexInput}
            maxLength={7}
            placeholder="#000000"
          />
        </div>

        <div className="TaskTypeDropdown__picker-search">
          <Input
            autoFocus
            size="small"
            placeholder="Поиск иконки..."
            value={iconSearch}
            onChange={(e) => setIconSearch(e.target.value)}
          />
        </div>

        <div className="TaskTypeDropdown__icon-grid">
          {filteredIcons.map(({ id, code }) => (
            <Tooltip key={id} title={id} mouseEnterDelay={0.6}>
              <button
                type="button"
                className={[
                  'TaskTypeDropdown__icon-cell',
                  code === pickerIcon &&
                    'TaskTypeDropdown__icon-cell--selected',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => handleIconSelect(code)}
              >
                <FontAwesomeIcon
                  icon={code as IconProp}
                  style={{ color: pickerColor }}
                />
              </button>
            </Tooltip>
          ))}
          {filteredIcons.length === 0 && (
            <span className="TaskTypeDropdown__icon-empty">Не найдено</span>
          )}
        </div>
      </div>
    );
  }

  // ── List view ──────────────────────────────────────────────────────────────

  return (
    <div className="TaskTypeDropdown">
      <div className="TaskTypeDropdown__list">
        {taskTypes.length === 0 && (
          <div className="TaskTypeDropdown__empty">Нет типов задач</div>
        )}
        {effectiveTypes.map((type) => {
          const originalType = taskTypes.find((t) => t.Id === type.Id)!;
          const isEditingName = editingNameId === type.Id;
          const isConfirmingDelete = confirmDeleteId === type.Id;
          return (
            <div
              key={type.Id}
              className={[
                'TaskTypeDropdown__item',
                type.Id === selectedId && 'TaskTypeDropdown__item--selected',
              ]
                .filter(Boolean)
                .join(' ')}
              role="option"
              aria-selected={type.Id === selectedId}
              tabIndex={0}
              onClick={() => {
                if (!isEditingName) handleSelect(type);
              }}
              onKeyDown={(e) => {
                if (isEditingName) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSelect(type);
                }
              }}
            >
              <button
                type="button"
                className="TaskTypeDropdown__icon-btn TaskTypeDropdown__icon-btn--inline"
                title="Изменить иконку"
                onClick={(e) => {
                  e.stopPropagation();
                  openPicker(type.Id);
                }}
              >
                <FontAwesomeIcon
                  icon={(type.IconId || DEFAULT_ICON) as IconProp}
                  style={{ color: type.Color || undefined }}
                />
              </button>

              {isEditingName ? (
                <input
                  type="text"
                  autoFocus
                  className="TaskTypeDropdown__item-name-input"
                  value={localNames[type.Id] ?? type.Name ?? ''}
                  onChange={(e) => handleNameChange(type.Id, e.target.value)}
                  onBlur={() => {
                    handleNameBlur(originalType);
                    setEditingNameId(null);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.currentTarget.blur();
                    if (e.key === 'Escape') {
                      setLocalNames((prev) => {
                        const next = { ...prev };
                        delete next[type.Id];
                        return next;
                      });
                      setEditingNameId(null);
                    }
                  }}
                />
              ) : (
                <span className="TaskTypeDropdown__item-name">
                  {type.Name || 'Без названия'}
                </span>
              )}

              {!isEditingName && (
                <div
                  className="TaskTypeDropdown__item-actions"
                  onClick={(e) => e.stopPropagation()}
                >
                  {isConfirmingDelete ? (
                    <>
                      <button
                        type="button"
                        className="TaskTypeDropdown__item-action TaskTypeDropdown__item-action--danger"
                        title="Подтвердить удаление"
                        disabled={isDeleting}
                        onClick={() => handleDelete(type.Id)}
                      >
                        <CheckOutlined />
                      </button>
                      <button
                        type="button"
                        className="TaskTypeDropdown__item-action"
                        title="Отмена"
                        disabled={isDeleting}
                        onClick={() => setConfirmDeleteId(null)}
                      >
                        <CloseOutlined />
                      </button>
                    </>
                  ) : (
                    <>
                      {type.Id === selectedId && (
                        <CheckOutlined className="TaskTypeDropdown__item-check" />
                      )}
                      <button
                        type="button"
                        className="TaskTypeDropdown__item-action"
                        title="Переименовать"
                        onClick={() => {
                          setConfirmDeleteId(null);
                          setEditingNameId(type.Id);
                        }}
                      >
                        <EditOutlined />
                      </button>
                      <button
                        type="button"
                        className="TaskTypeDropdown__item-action TaskTypeDropdown__item-action--danger"
                        title="Удалить тип"
                        onClick={() => setConfirmDeleteId(type.Id)}
                      >
                        <DeleteOutlined />
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="TaskTypeDropdown__divider" />

      <div className="TaskTypeDropdown__new">
        <div className="TaskTypeDropdown__new-row">
          <button
            type="button"
            className="TaskTypeDropdown__icon-btn TaskTypeDropdown__icon-btn--placeholder"
            title="Выбрать иконку"
            onClick={() => openPicker('new')}
          >
            <FontAwesomeIcon
              icon={draftIcon as IconProp}
              style={{ color: draftColor }}
            />
          </button>
          <input
            type="text"
            className="TaskTypeDropdown__new-input"
            placeholder="Новый тип задачи..."
            value={newName}
            disabled={isCreating}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={handleNewNameKeyDown}
          />
          <button
            type="button"
            className="TaskTypeDropdown__new-create-btn"
            title="Создать тип"
            disabled={!canCreate}
            onClick={handleCreate}
          >
            <PlusOutlined />
          </button>
        </div>
      </div>
    </div>
  );
};
